import { FC, useEffect, useState } from "react";
import { Empty, SearchInput, Spin, Tag } from "../../design-system";
import { listOrganizations } from "../../common/api/AdminDashboardClient";
import { Organization, OrganizationStatus } from "../../types/AdminDashboardTypes";
import { handleError } from "../../common/ErrorHandling";
import { formatDateMonthName } from "../date/DateOp";
import { useImpersonateOrganization } from "./useImpersonateOrganization";
import classes from "./ImpersonateView.module.less";

// Demo orgs are the ones impersonated most, so they sit on top of the list.
const isDemo = (org: Organization) => org.name.toLowerCase().includes("demo");

const byNewest = (a: Organization, b: Organization) =>
    Date.parse(b.recordCreationDate) - Date.parse(a.recordCreationDate);

/** Super admin page: pick an organization to act as its bot user. */
export const ImpersonateView: FC = () => {
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [query, setQuery] = useState("");
    const { impersonate, impersonatingId } = useImpersonateOrganization();

    useEffect(() => {
        listOrganizations().then(setOrganizations).catch(handleError);
    }, []);

    const needle = query.trim().toLowerCase();
    const shown = (organizations ?? [])
        .filter(
            (org) =>
                !needle ||
                org.name.toLowerCase().includes(needle) ||
                org.domain.toLowerCase().includes(needle)
        )
        .sort((a, b) => Number(isDemo(b)) - Number(isDemo(a)) || byNewest(a, b));

    return (
        <div className={classes.container}>
            <h2 className={classes.title}>Impersonate</h2>
            <div className={classes.subtitle}>
                Act as an organization's bot user. Demo organizations come first, then the newest.
            </div>
            <SearchInput
                className={classes.search}
                autoFocus
                placeholder="Search organizations"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />
            {!organizations ? (
                <Spin />
            ) : shown.length === 0 ? (
                <Empty description="No organization matches" />
            ) : (
                <ul className={classes.list}>
                    {shown.map((org) => (
                        <li key={org.id}>
                            <button
                                type="button"
                                className={classes.row}
                                disabled={impersonatingId !== undefined}
                                onClick={() => impersonate(org.id)}
                            >
                                <span className={classes.name}>
                                    {org.name}
                                    {isDemo(org) && <Tag color="blue">Demo</Tag>}
                                    {org.status !== OrganizationStatus.ACTIVE && (
                                        <Tag color="red">Inactive</Tag>
                                    )}
                                </span>
                                <span className={classes.meta}>
                                    {org.domain}, created{" "}
                                    {formatDateMonthName(org.recordCreationDate)}
                                </span>
                                {impersonatingId === org.id && (
                                    <Spin size="small" className={classes.spinner} />
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
